import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogFooter } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { StatusOptionRows } from "@/components/board/StatusOptionRows";
import {
  cleanOptions,
  hasBlankExistingLabel,
  newOptionId,
  optionsChanged,
} from "@/lib/statusOptions";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { ViewColumn } from "@/lib/boardView";
import type { StatusOption, UUID } from "@/lib/types";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  boardId: UUID;
  column: ViewColumn;
}

/**
 * Manage the OPTIONS of a single status column (project boards + ticket CATEGORY
 * columns): add / rename / recolor / archive. "Remove" is archive — the id stays
 * in `config.options` so cells that already used it keep resolving (a hard delete
 * would orphan them). Saves via the same `PATCH /columns/{id}` read-modify-write
 * the ticket settings panel uses — no backend change. The action-driven workflow
 * Status column never opens this (gated out in ColumnMenu).
 */
export function StatusOptionsDialog({ open, onOpenChange, boardId, column }: Props) {
  const qc = useQueryClient();
  const originalOptions = (column.raw?.config?.options ?? []) as StatusOption[];
  const required = Boolean(column.raw?.config?.required);
  const [draft, setDraft] = useState<StatusOption[]>([]);
  // Snapshot of the options as they were when the dialog opened. The change-diff
  // runs against THIS, not the live column — so opening the dialog and saving
  // without touching anything never writes (which would otherwise clobber a
  // concurrent edit by another admin received over WS while it sat open).
  const seedRef = useRef<StatusOption[]>([]);

  useEffect(() => {
    if (!open) return;
    const seed = originalOptions.map((o) => ({ ...o }));
    seedRef.current = seed;
    setDraft(seed.map((o) => ({ ...o })));
    // Reseed only on open so a live WS column update mid-edit can't clobber edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const save = useMutation({
    mutationFn: async () => {
      if (hasBlankExistingLabel(draft, originalOptions)) {
        throw new Error(t.ticketSettingsBlankLabel);
      }
      const cleaned = cleanOptions(draft);
      // Diff against the open-time snapshot: no local edits ⇒ no PATCH.
      if (!optionsChanged(cleaned, seedRef.current)) return;
      await api.updateColumn(column.id as UUID, {
        config: { ...(column.raw?.config ?? {}), options: cleaned },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.board(boardId) });
      showToast(t.ticketSettingsSaved, "success");
      onOpenChange(false);
    },
    onError: (e) => showToast(e instanceof Error ? e.message : t.unknownError, "error"),
  });

  function setOpt(idx: number, patch: Partial<StatusOption>) {
    setDraft((prev) => prev.map((o, i) => (i === idx ? { ...o, ...patch } : o)));
  }
  function addOpt() {
    setDraft((prev) => [...prev, { id: newOptionId(), label: "", color: "slate" }]);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !save.isPending && onOpenChange(o)}
      title={t.manageStatusOptions}
      description={column.name}
      width={460}
    >
      <div style={{ maxHeight: "58vh", overflowY: "auto", paddingRight: 2 }}>
        <StatusOptionRows options={draft} required={required} onChange={setOpt} onAdd={addOpt} />
        <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 12 }}>
          {t.ticketSettingsArchiveHint}
        </div>
      </div>
      <DialogFooter>
        <Button
          type="button"
          variant="ghost"
          size="md"
          onClick={() => onOpenChange(false)}
          disabled={save.isPending}
        >
          {t.cancel}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="md"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          {save.isPending ? t.saving : t.save}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
